import { AdvancedSearchUXProvider } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { DocumentSearchDataProvider } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useDocumentSearchSubmitRefresh } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchSubmitRefresh'
import { SearchHistoryItem } from 'features/knowledgeDocumentViewer/types'
import React, { useMemo } from 'react'

import { UnifiedSearchLanding } from './UnifiedSearchLanding'
import { UnifiedSearchResults } from './UnifiedSearchResults'

interface AdvancedSearchPageProps {
  siteId: string
  tabId?: string
  isActive?: boolean
  searchState: SearchHistoryItem['searchState']
  /** When set, show only these documents (no search bar). Optionally pass unitId to resolve IDs when this is empty. */
  linkedDocIds?: string[]
  unitId?: string
}

interface AdvancedSearchUXProviderWithRefreshProps {
  children: React.ReactNode
  siteId: string
  tabId?: string
  isActive: boolean
  searchState: SearchHistoryItem['searchState']
  isLinkedDocsMode: boolean
}

function AdvancedSearchUXProviderWithRefresh({
  children,
  siteId,
  tabId,
  isActive,
  searchState,
  isLinkedDocsMode,
}: AdvancedSearchUXProviderWithRefreshProps): React.ReactElement {
  const onSearchSubmitted = useDocumentSearchSubmitRefresh()

  return (
    <AdvancedSearchUXProvider
      siteId={siteId}
      tabId={tabId}
      isActive={isActive}
      searchState={searchState}
      isLinkedDocsMode={isLinkedDocsMode}
      onSearchSubmitted={onSearchSubmitted}>
      {children}
    </AdvancedSearchUXProvider>
  )
}

export function AdvancedSearchPage({
  siteId,
  tabId,
  isActive = true,
  searchState,
  linkedDocIds,
  unitId,
}: AdvancedSearchPageProps) {
  const isLinkedDocsMode = (linkedDocIds?.length ?? 0) > 0 || !!unitId
  const hasQuery = searchState.query.trim().length > 0
  const browseAll = searchState.browseAll === true
  const showResults = isLinkedDocsMode || hasQuery || browseAll

  const linkedDocsSearchState = useMemo(
    () =>
      isLinkedDocsMode
        ? { ...searchState, contentFilter: 'documents' as const }
        : searchState,
    [isLinkedDocsMode, searchState]
  )

  return (
    <DocumentSearchDataProvider
      siteId={siteId}
      tabId={tabId}
      searchQuery={searchState.query}
      contentFilter={linkedDocsSearchState.contentFilter}
      searchMode={linkedDocsSearchState.searchMode ?? 'normal'}
      linkedDocIds={linkedDocIds}
      unitId={unitId}
      browseAll={browseAll}
      agenticSearch={searchState.agenticSearch}>
      <AdvancedSearchUXProviderWithRefresh
        siteId={siteId}
        tabId={tabId}
        isActive={isActive}
        searchState={linkedDocsSearchState}
        isLinkedDocsMode={isLinkedDocsMode}>
        {showResults ? <UnifiedSearchResults /> : <UnifiedSearchLanding />}
      </AdvancedSearchUXProviderWithRefresh>
    </DocumentSearchDataProvider>
  )
}
